import {
  Button,
  Flex,
  Grid,
  SelectField,
  TextAreaField,
  TextField,
} from "@aws-amplify/ui-react";
import { useEffect, useState } from "react";
import LocalizedStrings from "react-localization";

const FAKE_AGRONOMISTS = [
  { id: "agr-001", name: "Ing. Juan Pérez" },
  { id: "agr-002", name: "Ing. María Gómez" },
  { id: "agr-003", name: "Ing. Carlos R." },
  { id: "agr-004", name: "Ing. Ana M." },
  { id: "agr-005", name: "Ing. Luis D." },
];

const FAKE_FARMS = [
  { id: "farm-001", name: "Finca Las Palmas" },
  { id: "farm-002", name: "Finca El Roble" },
  { id: "farm-003", name: "Finca La Esperanza" },
  { id: "farm-004", name: "Finca San José" },
  { id: "farm-005", name: "Finca Santa Rita" },
];

const FAKE_LOTS_BY_FARM = {
  "farm-001": [
    { id: "lot-001A", name: "Lote A" },
    { id: "lot-001B", name: "Lote B" },
    { id: "lot-001C", name: "Lote C" },
    { id: "lot-001D", name: "Lote D" },
    { id: "lot-001E", name: "Lote E" },
  ],
  "farm-002": [
    { id: "lot-002A", name: "Lote A" },
    { id: "lot-002B", name: "Lote B" },
    { id: "lot-002C", name: "Lote C" },
    { id: "lot-002D", name: "Lote D" },
    { id: "lot-002E", name: "Lote E" },
  ],
  // fallback for other farms
};

const STATUS_OPTIONS = ["", "Activa", "Cerrada", "Ejecutada"];

const TechnicalVisitReportForm = ({
  onSubmit,
  type = "create",
  initialValues = {},
  onCancel,
}) => {
  const defaultState = {
    code: "",
    reportNumber: "",
    date: "",
    performedById: "",
    performedByName: "",
    farmId: "",
    farmName: "",
    lotId: "",
    lotName: "",
    photoEvidence: "",
    recommendations: "",
    description: "",
    status: "",
    observations: "",
    createdAt: "",
  };

  const [form, setForm] = useState({ ...defaultState, ...initialValues });
  const [availableLots, setAvailableLots] = useState([]);

  useEffect(() => {
    setForm({ ...defaultState, ...initialValues });

    // load lots for selected farm (fake)
    if (initialValues.farmId) {
      const lots = FAKE_LOTS_BY_FARM[initialValues.farmId] || [];
      setAvailableLots(lots);
      if (initialValues.lotId && !initialValues.lotName) {
        const foundLot = lots.find((l) => l.id === initialValues.lotId);
        if (foundLot) setForm((f) => ({ ...f, lotName: foundLot.name }));
      }
    }
    if (initialValues.performedById && !initialValues.performedByName) {
      const found = FAKE_AGRONOMISTS.find(
        (a) => a.id === initialValues.performedById
      );
      if (found) setForm((f) => ({ ...f, performedByName: found.name }));
    }
    if (initialValues.farmId && !initialValues.farmName) {
      const found = FAKE_FARMS.find((f) => f.id === initialValues.farmId);
      if (found) setForm((s) => ({ ...s, farmName: found.name }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues]);

  const handleFarmChange = (farmId) => {
    const farm = FAKE_FARMS.find((f) => f.id === farmId);
    const lots = FAKE_LOTS_BY_FARM[farmId] || [];
    setAvailableLots(lots);
    setForm({
      ...form,
      farmId,
      farmName: farm ? farm.name : "",
      lotId: "",
      lotName: "",
    });
  };

  const handleLotChange = (lotId) => {
    const lot = (availableLots || []).find((l) => l.id === lotId);
    setForm({ ...form, lotId, lotName: lot ? lot.name : "" });
  };

  const onSubmitForm = () => {
    const payload = { ...form };
    // generate temporary code and reportNumber if creating
    if (type === "create" && !payload.code) {
      payload.code = String(Math.floor(10000 + Math.random() * 90000));
    }
    if (type === "create" && !payload.reportNumber) {
      payload.reportNumber = `R-${Math.floor(1000 + Math.random() * 9000)}`;
    }
    if (!payload.createdAt)
      payload.createdAt = new Date().toISOString().slice(0, 10);
    onSubmit && onSubmit(payload);
  };

  return (
    <Grid rowGap="12px" columnGap="12px" padding="20px">
      <Grid gap="12px" templateColumns={{ base: "1fr", large: "1fr 1fr" }}>
        <TextField
          name="code"
          label={strings.fields.code.label}
          value={form.code || ""}
          isReadOnly
        />
        <TextField
          name="date"
          label={strings.fields.date.label}
          placeholder="YYYY-MM-DD"
          value={form.date || ""}
          onChange={(e) => setForm({ ...form, date: e.target.value })}
        />
      </Grid>

      <Grid gap="12px" templateColumns={{ base: "1fr", large: "1fr 1fr 1fr" }}>
        <SelectField
          name="performedById"
          label={strings.fields.performedById.label}
          value={form.performedById || ""}
          onChange={(e) => {
            const val = e.target.value;
            const found = FAKE_AGRONOMISTS.find((a) => a.id === val);
            setForm({
              ...form,
              performedById: val,
              performedByName: found ? found.name : "",
            });
          }}
        >
          <option value="">--</option>
          {FAKE_AGRONOMISTS.map((a) => (
            <option key={a.id} value={a.id}>
              {a.name}
            </option>
          ))}
        </SelectField>
        <SelectField
          name="farmId"
          label={strings.fields.farmId.label}
          value={form.farmId || ""}
          onChange={(e) => handleFarmChange(e.target.value)}
        >
          <option value="">--</option>
          {FAKE_FARMS.map((f) => (
            <option key={f.id} value={f.id}>
              {f.name}
            </option>
          ))}
        </SelectField>

        <SelectField
          name="lotId"
          label={strings.fields.lotId.label}
          value={form.lotId || ""}
          onChange={(e) => handleLotChange(e.target.value)}
        >
          <option value="">--</option>
          {availableLots.map((l) => (
            <option key={l.id} value={l.id}>
              {l.name}
            </option>
          ))}
        </SelectField>
      </Grid>

      <TextField
        name="photoEvidence"
        label={strings.fields.photoEvidence.label}
        placeholder="foto1.jpg, foto2.jpg"
        value={
          Array.isArray(form.photoEvidence)
            ? form.photoEvidence.join(", ")
            : form.photoEvidence || ""
        }
        onChange={(e) => setForm({ ...form, photoEvidence: e.target.value })}
      />

      <TextAreaField
        name="recommendations"
        label={strings.fields.recommendations.label}
        value={form.recommendations || ""}
        onChange={(e) => setForm({ ...form, recommendations: e.target.value })}
      />

      <TextAreaField
        name="description"
        label={strings.fields.description.label}
        value={form.description || ""}
        onChange={(e) => setForm({ ...form, description: e.target.value })}
      />

      <SelectField
        name="status"
        label={strings.fields.status.label}
        value={form.status || ""}
        onChange={(e) => setForm({ ...form, status: e.target.value })}
      >
        {STATUS_OPTIONS.map((s) => (
          <option key={s} value={s}>
            {s || "--"}
          </option>
        ))}
      </SelectField>

      <TextAreaField
        name="observations"
        label={strings.fields.observations.label}
        value={form.observations || ""}
        onChange={(e) => setForm({ ...form, observations: e.target.value })}
      />

      <Flex justifyContent="end">
        <Button
          children={strings.fields.buttons.reset}
          onClick={() => setForm(defaultState)}
        />
        <Button
          children={
            type === "create"
              ? strings.fields.buttons.create
              : strings.fields.buttons.save
          }
          onClick={onSubmitForm}
          variation="primary"
        />
      </Flex>
    </Grid>
  );
};

const strings = new LocalizedStrings({
  es: {
    fields: {
      code: { label: "Código" },
      reportNumber: { label: "Número de Informe" },
      date: { label: "Fecha" },
      performedById: { label: "Persona que realiza (Agrónomo)" },
      farmId: { label: "Finca" },
      lotId: { label: "Lote" },
      photoEvidence: { label: "Evidencia fotográfica" },
      recommendations: { label: "Recomendaciones" },
      description: { label: "Descripción" },
      status: { label: "Estado" },
      observations: { label: "Observaciones" },
      buttons: {
        create: "Crear",
        save: "Guardar",
        reset: "Restablecer",
        cancel: "Cancelar",
      },
    },
  },
});

export default TechnicalVisitReportForm;
